import { Controller } from "@hotwired/stimulus"

/**
 * Row Actions Menu Controller
 *
 * Manages the overflow menu (three-dots) for table row actions.
 * Uses fixed positioning to escape overflow-hidden containers.
 *
 * Features:
 * - Toggle menu visibility
 * - Fixed positioning to escape overflow containers
 * - Close on click outside
 * - Close on Escape key
 * - Proper ARIA attributes for accessibility
 *
 * Usage:
 *   <div data-controller="row-actions-menu">
 *     <button data-action="click->row-actions-menu#toggle"
 *             data-row-actions-menu-target="trigger"
 *             aria-expanded="false"
 *             aria-haspopup="true">
 *       <i class="fa-solid fa-ellipsis-vertical"></i>
 *     </button>
 *     <div data-row-actions-menu-target="menu" class="hidden">
 *       ... menu items ...
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["menu", "trigger"]

  connect() {
    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
    this.menuMovedToBody = false

    // Sauvegarder la référence au menu car elle sera perdue après le déplacement vers body
    this.menuElement = this.menuTarget
  }

  disconnect() {
    this.removeGlobalListeners()
    this.restoreMenuToOriginalPosition()
  }

  toggle(event) {
    event.stopPropagation()

    if (this.isOpen()) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.moveMenuToBody()
    // Rendre visible temporairement pour mesurer les dimensions
    this.menuElement.classList.remove("hidden")
    this.menuElement.style.visibility = "hidden"
    // Forcer un reflow pour obtenir les dimensions correctes
    this.menuElement.offsetHeight
    this.positionMenu()
    // Rendre réellement visible
    this.menuElement.style.visibility = ""
    this.updateAriaExpanded(true)
    this.addGlobalListeners()
  }

  close() {
    this.menuElement.classList.add("hidden")
    this.updateAriaExpanded(false)
    this.removeGlobalListeners()
  }

  isOpen() {
    return !this.menuElement.classList.contains("hidden")
  }

  moveMenuToBody() {
    if (this.menuMovedToBody) return

    // Sauvegarder la position originale
    this.originalParent = this.menuElement.parentNode
    this.originalNextSibling = this.menuElement.nextSibling

    // Déplacer le menu dans le body
    document.body.appendChild(this.menuElement)
    this.menuMovedToBody = true

    // Appliquer les styles fixed
    this.menuElement.style.position = "fixed"
    this.menuElement.style.zIndex = "1080"
  }

  restoreMenuToOriginalPosition() {
    if (!this.menuMovedToBody || !this.menuElement) return

    // Nettoyer les styles
    this.menuElement.style.position = ""
    this.menuElement.style.zIndex = ""
    this.menuElement.style.top = ""
    this.menuElement.style.left = ""
    this.menuElement.style.right = ""

    // Restaurer la position originale dans le DOM
    if (this.originalNextSibling) {
      this.originalParent.insertBefore(this.menuElement, this.originalNextSibling)
    } else {
      this.originalParent.appendChild(this.menuElement)
    }

    this.menuMovedToBody = false
  }

  positionMenu() {
    if (!this.hasTriggerTarget) return

    const triggerRect = this.triggerTarget.getBoundingClientRect()
    const menuRect = this.menuElement.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const padding = 8

    // Calculer la position verticale (en dessous du bouton par défaut)
    let top = triggerRect.bottom + padding

    // Si le menu dépasse en bas, le placer au-dessus
    if (top + menuRect.height > viewportHeight - padding) {
      top = triggerRect.top - menuRect.height - padding
    }

    // Calculer la position horizontale (aligné à droite du bouton)
    let left = triggerRect.right - menuRect.width

    // Si le menu dépasse à gauche, l'aligner à gauche du bouton
    if (left < padding) {
      left = triggerRect.left
    }

    // Si le menu dépasse à droite
    if (left + menuRect.width > viewportWidth - padding) {
      left = viewportWidth - menuRect.width - padding
    }

    this.menuElement.style.top = `${top}px`
    this.menuElement.style.left = `${left}px`
  }

  updateAriaExpanded(expanded) {
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", expanded.toString())
    }
  }

  addGlobalListeners() {
    document.addEventListener("click", this.handleClickOutside)
    document.addEventListener("keydown", this.handleKeydown)
    window.addEventListener("scroll", this.handleScroll, true)
    window.addEventListener("resize", this.handleScroll)
  }

  removeGlobalListeners() {
    document.removeEventListener("click", this.handleClickOutside)
    document.removeEventListener("keydown", this.handleKeydown)
    window.removeEventListener("scroll", this.handleScroll, true)
    window.removeEventListener("resize", this.handleScroll)
  }

  handleClickOutside(event) {
    // Vérifier si le clic est sur le trigger ou dans le menu
    const isClickOnTrigger = this.hasTriggerTarget && this.triggerTarget.contains(event.target)
    const isClickInMenu = this.menuElement && this.menuElement.contains(event.target)

    if (!isClickOnTrigger && !isClickInMenu) {
      this.close()
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close()
      if (this.hasTriggerTarget) {
        this.triggerTarget.focus()
      }
    }
  }

  handleScroll() {
    if (this.isOpen()) {
      this.positionMenu()
    }
  }
}
